import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Box } from '@mui/material';
import { RecipeStore } from './stores';
import RecipeGroup from './RecipeGroup';
import './recipe.css';

import { RecipeState } from './types';

export interface RecipeProps {
  store?: RecipeStore; // Optional - only in edit mode
  data?: RecipeState; // Optional - only in readonly mode
  readonly: boolean;
  onChange?: () => void;
  onRuntimeCalculated?: (runtime: number) => void;
  /**
   * Whether this instance owns the MobX `store` lifecycle and should dispose it on unmount.
   * Defaults to `true`. Set to `false` when rendering the SAME store in a secondary view
   * (e.g. a full-screen popup alongside an inline editor) so unmounting that secondary view
   * does not tear down the reactions still used by the owning instance.
   */
  ownsStore?: boolean;
  /** Full-screen popup: taller scroll region and sticky column headers. */
  layout?: 'default' | 'fullView';
}

/**
 * Recipe component - supports both MobX (edit mode) and plain data (readonly mode).
 *
 * In edit mode: Uses MobX stores for reactive formula calculations
 * In readonly mode: Uses plain RecipeState data (already calculated from backend)
 *
 * Runtime calculation is now handled by MobX reaction in RecipeStore (not useEffect).
 */
const Recipe = observer(({ store, data, readonly, ownsStore = true, layout = 'default' }: RecipeProps) => {
  // Cleanup runtime reaction when component unmounts or store changes.
  // Skip when this instance does not own the store (secondary/popup view sharing the owner's store).
  useEffect(() => {
    return () => {
      if (store && ownsStore) {
        store.dispose();
      }
    };
  }, [store, ownsStore]);

  // Use MobX store in edit mode, plain data in readonly mode
  const groups = store?.groups || data?.groups || [];

  if (groups.length === 0) {
    return null;
  }

  return (
    <Box className={`recipe-container${layout === 'fullView' ? ' recipe-layout-full-view' : ''}`}>
      {groups.map((group, groupIndex) => {
        // In edit mode: pass MobX GroupStore
        // In readonly mode: pass plain RecipeGroupState
        return (
          <RecipeGroup
            key={group.symbol}
            group={group}
            groupIndex={groupIndex}
            readonly={readonly}
            recipeContext={data}
            onAddGroup={store && group.canAddGroup && !readonly ? () => store.addGroup(groupIndex) : undefined}
            onRemoveGroup={
              store && group.canRemoveGroup && !readonly && groups.length > 1
                ? () => store.removeGroup(groupIndex)
                : undefined
            }
          />
        );
      })}
    </Box>
  );
});

Recipe.displayName = 'Recipe';

export default Recipe;
